/**
 * Sunucu bağlantısı.
 *
 * Oynatıcı iki modda çalışır:
 *
 *  - **Gösterim**: adreste jeton yok. Hiçbir şey kaydedilmez, rapor
 *    ekranda gösterilir. Tanıtım ve deneme için.
 *  - **Bağlı**: adreste `?t=<jeton>` var. Jetonu psikolog ölçümü
 *    başlatırken üretir. Denemeler sunucuya yazılır, puanlama sunucuda
 *    yapılır ve rapor ÇOCUĞA GÖSTERİLMEZ — uzmanın paneline gider.
 *
 * Ölçüm, ağ yüzünden durmaz: denemeler önce yerele yazılır, arka planda
 * gönderilir. Bağlantı koparsa ölçüm devam eder ve veri birikir.
 */

export interface SunucuDeneme {
  taskCode: string
  domain: string
  trialIndex: number
  stimulusAtMs: number
  responseAtMs: number | null
  isTarget: boolean
  difficultyLevel: number
  framesDropped: number
  presentationLagMs: number | null
  outcome: string | null
  stimulus?: Record<string, unknown>
  /** Cocugun verdigi cevap ve beklenen cevap — rapordaki deneme detayi icin */
  response?: string | string[]
  expected?: string | string[]
}

const DB_ADI = 'nirengi-oynatici'
const DEPO = 'bekleyen'

/* ------------------------------------------------------------------ *
 * Yerel dayanıklılık
 *
 * Denemeler önce IndexedDB'ye yazılır. Tarayıcı kapansa, sayfa yenilense
 * ya da ağ gitse bile gönderilmemiş deneme kaybolmaz; bir sonraki
 * açılışta gönderilir.
 * ------------------------------------------------------------------ */

function db(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const istek = indexedDB.open(DB_ADI, 1)
      istek.onupgradeneeded = () => {
        const d = istek.result
        if (!d.objectStoreNames.contains(DEPO)) {
          d.createObjectStore(DEPO, { keyPath: 'anahtar' })
        }
      }
      istek.onsuccess = () => resolve(istek.result)
      istek.onerror = () => resolve(null)
    } catch {
      // Gizli sekmede ya da depolama kapalıyken IndexedDB patlayabilir.
      // Ölçümün bu yüzden durmaması gerekir; yerel yedek olmadan devam.
      resolve(null)
    }
  })
}

async function yerelYaz(olcumId: string, denemeler: SunucuDeneme[]): Promise<void> {
  const d = await db()
  if (!d) return
  await new Promise<void>((resolve) => {
    const t = d.transaction(DEPO, 'readwrite')
    const depo = t.objectStore(DEPO)
    for (const dn of denemeler) {
      depo.put({ anahtar: `${olcumId}:${dn.taskCode}:${dn.trialIndex}`, olcumId, deneme: dn })
    }
    t.oncomplete = () => resolve()
    t.onerror = () => resolve()
  })
}

async function yerelSil(olcumId: string, denemeler: SunucuDeneme[]): Promise<void> {
  const d = await db()
  if (!d) return
  await new Promise<void>((resolve) => {
    const t = d.transaction(DEPO, 'readwrite')
    const depo = t.objectStore(DEPO)
    for (const dn of denemeler) depo.delete(`${olcumId}:${dn.taskCode}:${dn.trialIndex}`)
    t.oncomplete = () => resolve()
    t.onerror = () => resolve()
  })
}

async function yerelOku(olcumId: string): Promise<SunucuDeneme[]> {
  const d = await db()
  if (!d) return []
  return new Promise((resolve) => {
    const t = d.transaction(DEPO, 'readonly')
    const istek = t.objectStore(DEPO).getAll()
    istek.onsuccess = () => {
      const hepsi = (istek.result ?? []) as { olcumId: string; deneme: SunucuDeneme }[]
      resolve(hepsi.filter((x) => x.olcumId === olcumId).map((x) => x.deneme))
    }
    istek.onerror = () => resolve([])
  })
}

/* ------------------------------------------------------------------ */

export interface OlcumBilgisi {
  id: string
  form: 'A' | 'B' | 'C'
  durum: string
  salt: string
  /** Yalnızca bu görevler koşulacak; null ise tam set */
  gorevler: string[] | null
}

export class Sunucu {
  private kuyruk: SunucuDeneme[] = []
  private gonderiliyor = false
  /** Art arda başarısız gönderim sayısı — geri çekilme için */
  private hata = 0
  private olcumId = ''

  constructor(private jeton: string) {}

  private get baslik(): HeadersInit {
    return { 'content-type': 'application/json', 'x-oynatma-jetonu': this.jeton }
  }

  /** Jeton geçerli mi, hangi ölçüme ait */
  async baslat(): Promise<OlcumBilgisi | null> {
    try {
      const r = await fetch('/api/oynat/olcum', { headers: this.baslik })
      if (!r.ok) return null
      const g = (await r.json()) as { olcum: OlcumBilgisi; salt: string }
      this.olcumId = g.olcum.id
      // Önceki oturumdan kalan gönderilmemiş denemeler varsa onlar da gider
      const kalan = await yerelOku(this.olcumId)
      if (kalan.length > 0) this.kuyruk.push(...kalan)
      return { ...g.olcum, salt: g.salt }
    } catch {
      return null
    }
  }

  async cihazBilgisi(device: Record<string, unknown>, yenilemeHz: number): Promise<void> {
    try {
      await fetch('/api/oynat/cihaz', {
        method: 'POST',
        headers: this.baslik,
        body: JSON.stringify({ device, yenilemeHz }),
      })
    } catch {
      // Cihaz bilgisi gidemezse ölçüm yine de sürer; sonunda tekrar denenir
    }
  }

  /**
   * Denemeyi kuyruğa alır. BEKLEMEZ: ölçümün ortasında ağ beklemek,
   * bir sonraki uyarıcıyı geciktirir ve ölçülen süreyi bozar.
   */
  async ekle(d: SunucuDeneme): Promise<void> {
    this.kuyruk.push(d)
    void yerelYaz(this.olcumId, [d])
    if (this.kuyruk.length >= 10) void this.gonder()
  }

  /** Kuyruğu gönderir. Başarısızsa veri kuyrukta kalır. */
  async gonder(): Promise<boolean> {
    if (this.gonderiliyor || this.kuyruk.length === 0) return true
    this.gonderiliyor = true
    const paket = this.kuyruk.slice(0, 50)
    try {
      const r = await fetch('/api/oynat/denemeler', {
        method: 'POST',
        headers: this.baslik,
        body: JSON.stringify({ denemeler: paket }),
      })
      if (!r.ok) throw new Error(String(r.status))
      this.kuyruk = this.kuyruk.slice(paket.length)
      void yerelSil(this.olcumId, paket)
      this.hata = 0
      return true
    } catch {
      this.hata++
      return false
    } finally {
      this.gonderiliyor = false
    }
  }

  get bekleyen(): number {
    return this.kuyruk.length
  }

  get bagliMi(): boolean {
    return this.hata < 3
  }

  /**
   * Kalanları gönderip ölçümü kapatır.
   *
   * Gönderemediği deneme kalırsa ölçümü BİTİRMEZ: eksik veriyle
   * puanlanmış bir ölçüm, eksik olduğu bilinmeyen bir ölçümdür.
   */
  async bitir(): Promise<{ tamam: boolean; eksik: number }> {
    for (let i = 0; i < 6 && this.kuyruk.length > 0; i++) {
      const ok = await this.gonder()
      if (!ok) await new Promise((r) => setTimeout(r, 400 * (i + 1)))
    }
    if (this.kuyruk.length > 0) return { tamam: false, eksik: this.kuyruk.length }

    try {
      const r = await fetch('/api/oynat/bitir', { method: 'POST', headers: this.baslik })
      return { tamam: r.ok, eksik: 0 }
    } catch {
      return { tamam: false, eksik: 0 }
    }
  }
}

/** Adresteki jeton */
export function jetonuOku(): string | null {
  const p = new URLSearchParams(location.search)
  return p.get('t')
}

/* ------------------------------------------------------------------ *
 * Egzersiz
 *
 * Ölçümden farklı: tek bir sayfa, tek bir gönderim. Denemeler gibi
 * biriktirme gerekmiyor — çocuk sayfayı bitirince bir kez yolluyoruz.
 * ------------------------------------------------------------------ */

export interface EgzersizOdevi {
  id: string
  kod: string
  surum: string
  duzey: number
  tohum: string
  bitti: boolean
}

export class EgzersizSunucu {
  constructor(private jeton: string) {}

  private get baslik(): HeadersInit {
    return { 'content-type': 'application/json', 'x-egzersiz-jetonu': this.jeton }
  }

  async odev(): Promise<EgzersizOdevi | null> {
    try {
      const r = await fetch('/api/oynat/egzersiz', { headers: this.baslik })
      if (!r.ok) return null
      const g = (await r.json()) as { odev: EgzersizOdevi }
      return g.odev
    } catch {
      return null
    }
  }

  /**
   * Ham cevabı yollar.
   *
   * Puanlama sunucuda yapılıyor; buradan skor GÖNDERİLMİYOR ve geri de
   * alınmıyor. Hata durumunda atmak önemli: çocuğa "bitti" demeden önce
   * kaydın gerçekten yazıldığını bilmek gerekiyor.
   */
  async bitir(cevap: unknown, sureMs: number): Promise<void> {
    const r = await fetch('/api/oynat/egzersiz/bitir', {
      method: 'POST',
      headers: this.baslik,
      body: JSON.stringify({ cevap, sureMs, device: cihazBilgisi() }),
    })
    if (!r.ok) throw new Error(`egzersiz gonderilemedi: ${r.status}`)
  }
}

function cihazBilgisi(): Record<string, unknown> {
  return {
    ua: navigator.userAgent,
    ekran: `${window.innerWidth}x${window.innerHeight}`,
    dokunmatik: navigator.maxTouchPoints > 0,
  }
}

/** Adres çubuğundaki egzersiz jetonu */
export function egzersizJetonunuOku(): string | null {
  return new URLSearchParams(location.search).get('e')
}

/* ------------------------------------------------------------------ */

export interface HaftaOdevi {
  id: string
  kod: string
  alan: string
  seviye: number
  yapildi: string | null
}

/**
 * Haftanın listesi.
 *
 * Hafta jetonu bir DİZİN: tek başına egzersiz açmıyor. Listeyi
 * veriyor ve istenen ödev için ayrı bir egzersiz jetonu üretiyor;
 * egzersizin kendisine giden yol değişmedi.
 */
export class HaftaSunucu {
  constructor(private jeton: string) {}

  private get baslik(): HeadersInit {
    return { 'content-type': 'application/json', 'x-hafta-jetonu': this.jeton }
  }

  async odevler(): Promise<HaftaOdevi[] | null> {
    try {
      const r = await fetch('/api/oynat/hafta', { headers: this.baslik })
      if (!r.ok) return null
      const g = (await r.json()) as { odevler: HaftaOdevi[] }
      return g.odevler
    } catch {
      return null
    }
  }

  /** Seçilen ödev için egzersiz jetonu */
  async ac(odevId: string): Promise<string | null> {
    try {
      const r = await fetch(`/api/oynat/hafta/${odevId}`, {
        method: 'POST',
        headers: this.baslik,
      })
      if (!r.ok) return null
      const g = (await r.json()) as { jeton: string }
      return g.jeton
    } catch {
      return null
    }
  }
}

/** Adres çubuğundaki hafta jetonu */
export function haftaJetonunuOku(): string | null {
  return new URLSearchParams(location.search).get('h')
}
